/**
 * Sub-spec 11 Plan 4 — customer waiver acceptance gate.
 *
 * Customers are redirected here by CustomerAuthGate when the tenant has a
 * waiver configured AND their last-accepted version is below the tenant's
 * current waiver_version. Submitting POST /api/customer/waiver/accept
 * snapshots the tenant version + signature text; the gate then allows them
 * through to the rest of /account.
 */
import { type CSSProperties, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { CustomerApiError, customerApi } from '../customer_api';
import { RenderWaiverHtml } from './WaiverAcceptHtml';

interface WaiverResponse {
  html: string | null;
  version: number;
}

export function WaiverAccept() {
  const navigate = useNavigate();
  const [waiver, setWaiver] = useState<WaiverResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    customerApi<WaiverResponse>('/api/customer/waiver')
      .then((res) => setWaiver(res))
      .catch((err) => {
        const msg =
          err instanceof CustomerApiError ? `Could not load waiver (${err.status})` : String(err);
        setLoadError(msg);
      });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!signature.trim() || !agreed) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await customerApi('/api/customer/waiver/accept', {
        method: 'POST',
        body: JSON.stringify({ signatureText: signature.trim() }),
      });
      navigate('/account');
    } catch (err) {
      setSubmitError(
        err instanceof CustomerApiError
          ? `Could not record signature (${err.status})`
          : 'Could not record signature. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const wrapperStyle: CSSProperties = {
    minHeight: '100dvh',
    background: 'var(--background, oklch(0.98 0.005 80))',
    color: 'var(--foreground, oklch(0.19 0.012 75))',
  };

  return (
    <BrandedShell>
      <div style={wrapperStyle}>
        <div className="mx-auto max-w-2xl px-4 pt-6 pb-20 sm:px-6 sm:pt-10">
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            Please review and sign our terms
          </h1>
          <p style={{ color: 'var(--muted-foreground, oklch(0.45 0.005 80))', fontSize: '0.9rem' }}>
            Before continuing, please read the waiver below and type your full name to sign.
          </p>

          {loadError !== null && (
            <p style={{ color: 'var(--destructive, oklch(0.55 0.18 30))', marginTop: '1rem' }}>
              {loadError}
            </p>
          )}

          {waiver?.html ? <RenderWaiverHtml html={waiver.html} /> : null}

          {waiver && waiver.html === null && (
            <p style={{ marginTop: '1.25rem' }}>
              This tenant has no waiver configured. <a href="/account">Continue</a>.
            </p>
          )}

          {waiver?.html && (
            <form
              onSubmit={handleSubmit}
              style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.4rem',
                  fontSize: '0.9rem',
                }}
              >
                Type your full name
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Jane Doe"
                  aria-label="Signature text"
                  style={{
                    padding: '0.55rem 0.75rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border, oklch(0.915 0.005 80))',
                    background: 'var(--surface, white)',
                    fontSize: '0.95rem',
                    fontFamily: 'cursive',
                  }}
                />
              </label>

              <label
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                  fontSize: '0.9rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  aria-label="I have read and agree"
                  style={{ marginTop: '0.2rem' }}
                />
                <span>I have read and agree to the terms above (version {waiver.version}).</span>
              </label>

              {submitError !== null && (
                <p style={{ color: 'var(--destructive, oklch(0.55 0.18 30))' }}>{submitError}</p>
              )}

              <div>
                <button
                  type="submit"
                  disabled={submitting || !agreed || signature.trim().length === 0}
                  style={{
                    padding: '0.7rem 1.4rem',
                    background: 'var(--primary, oklch(0.4 0.15 254))',
                    color: 'white',
                    borderRadius: '999px',
                    border: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting || !agreed || signature.trim().length === 0 ? 0.6 : 1,
                  }}
                >
                  {submitting ? 'Recording…' : 'I accept'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </BrandedShell>
  );
}
